import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Container, Form, Button } from 'react-bootstrap';
import {
  useActionData,
  useNavigation,
  useSubmit,
  useNavigate,
} from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { emailFieldRequired } from '@/common/utilities/formFieldSchemas.ts';
import { useFlashMessage } from '@/common/contexts/FlashMessageContext.tsx';

const schema = z.object({
  email: emailFieldRequired,
});

type FormData = z.infer<typeof schema>;

interface ActionData {
  error?: string;
  success?: boolean;
  flash?: {
    message: string;
    type: 'success' | 'danger' | 'info';
  };
}

const PasskeyLoginForm: React.FC = () => {
  const actionData = useActionData() as ActionData | undefined;
  const navigation = useNavigation();
  const navigate = useNavigate();
  const submit = useSubmit();
  const { setFlashMessage } = useFlashMessage();

  const isLoading = navigation.state === 'submitting';

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: '',
    },
    mode: 'onChange',
  });

  useEffect(() => {
    if (actionData?.success) {
      setFlashMessage(
        actionData.flash?.message || 'Login successful!',
        actionData.flash?.type || 'success'
      );
      navigate('/portal');
    } else if (actionData?.error) {
      setFlashMessage(actionData.error, 'danger');
    } else if (actionData?.flash) {
      setFlashMessage(actionData.flash.message, actionData.flash.type);
    }
  }, [actionData, setFlashMessage, navigate]);

  const onSubmit = (data: FormData) => {
    const formData = new FormData();
    formData.append('email', data.email);

    void submit(formData, {
      method: 'post',
    });
  };

  return (
    <Container className="py-5">
      <div className="row justify-content-center">
        <div className="col-md-6">
          <h1 className="mb-4 text-center">Sign In</h1>

          <Form onSubmit={handleSubmit(onSubmit)}>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter your email"
                isInvalid={!!errors.email}
                {...register('email')}
              />
              <Form.Control.Feedback type="invalid">
                {errors.email?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <div className="d-grid">
              <Button
                variant="primary"
                type="submit"
                disabled={isLoading}
                size="lg"
              >
                {isLoading ? 'Signing In...' : 'Sign In with Passkey'}
              </Button>
            </div>
          </Form>

          <div className="mt-4 text-center">
            <p className="text-muted">
              Passkeys use your device's built-in authentication (like
              fingerprint or face recognition) to securely log you in without
              having to remember complex passwords.
            </p>
          </div>
        </div>
      </div>
    </Container>
  );
};

export default PasskeyLoginForm;
